<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import VirtualScroller from 'primevue/virtualscroller';
import ProgressSpinner from 'primevue/progressspinner';
import { yieldAnimationFrame } from '../utils/yield-to-paint';
import DeviceOptionContent from './DeviceOptionContent.vue';
import {
  filterTrackerPickerDevices,
  findTrackerPickerDeviceIndex,
  firstTrackerPickerScrollTargetId,
  nextTrackerPickerHighlightIndex,
  resolveTrackerPickerListScrollAction,
  trackerPickerValueFromDevice,
  type TrackerPickerDevice,
  type TrackerPickerValue,
} from '../utils/tracker-picker';

const DEVICE_ROW_PX = 84;
const SPINNER_STROKE_WIDTH = '2';
const SPINNER_STYLE_MEDIUM = { width: '2.5rem', height: '2.5rem' } as const;

const modelValue = defineModel<TrackerPickerValue[]>({ default: () => [] });

const props = withDefaults(
  defineProps<{
    devices?: readonly TrackerPickerDevice[];
    loading?: boolean;
    placeholder?: string;
    scrollHeight?: string;
    /** Clears search when set to false (e.g. hidden wizard step). */
    active?: boolean;
    /** Max trackers selectable; 1 = single-select (ENG-2710), unset = unlimited (ENG-2580). */
    maxSelection?: number | null;
    loadingLabel?: string;
    emptyLabel?: string;
    noMatchLabel?: (query: string) => string;
  }>(),
  {
    devices: () => [],
    loading: false,
    placeholder: 'Search by name, serial, IMEI, or ID…',
    scrollHeight: '420px',
    active: true,
    maxSelection: 1,
    loadingLabel: 'Loading trackers…',
    emptyLabel: 'No active trackers found.',
    noMatchLabel: (query: string) => `No trackers match “${query}”.`,
  },
);

const searchQuery = ref('');
const searchTrim = computed(() => searchQuery.value.trim());
const highlightedIndex = ref(-1);
const searchInputRef = ref<{ $el?: HTMLElement } | null>(null);
const scrollerRef = ref<{
  scrollToIndex: (index: number, behavior?: string) => void;
} | null>(null);

watch(
  () => props.active,
  (active) => {
    if (!active) {
      searchQuery.value = '';
      highlightedIndex.value = -1;
    }
  },
);

watch(searchQuery, () => {
  highlightedIndex.value = -1;
});

const filteredDevices = computed(() =>
  filterTrackerPickerDevices(props.devices, searchQuery.value),
);

const selectedIdSet = computed(
  () => new Set(modelValue.value.map((item) => item.value)),
);

function isSelected(deviceId: number): boolean {
  return selectedIdSet.value.has(deviceId);
}

function canAddMoreSelections(): boolean {
  const max = props.maxSelection;
  if (max == null) return true;
  return modelValue.value.length < max;
}

function selectDevice(device: TrackerPickerDevice) {
  const nextValue = trackerPickerValueFromDevice(device);
  if (props.maxSelection === 1) {
    modelValue.value = [nextValue];
  } else if (!isSelected(device.id) && canAddMoreSelections()) {
    modelValue.value = [...modelValue.value, nextValue];
  }
  highlightedIndex.value = findTrackerPickerDeviceIndex(
    filteredDevices.value,
    device.id,
  );
}

function toggleSelection(device: TrackerPickerDevice) {
  if (isSelected(device.id)) {
    modelValue.value = modelValue.value.filter((item) => item.value !== device.id);
    if (modelValue.value.length === 0) {
      highlightedIndex.value = -1;
    }
    return;
  }
  selectDevice(device);
}

function isKeyboardHighlighted(index: number): boolean {
  return highlightedIndex.value === index;
}

function scrollListToIndex(index: number) {
  if (index < 0) return;
  scrollerRef.value?.scrollToIndex(index, 'auto');
}

async function afterScrollerPainted() {
  await nextTick();
  await yieldAnimationFrame();
}

async function scrollToSelectedDevice() {
  if (props.loading || !props.active || filteredDevices.value.length === 0) {
    return;
  }

  const selectedId = firstTrackerPickerScrollTargetId(modelValue.value);
  if (selectedId == null) return;

  const index = findTrackerPickerDeviceIndex(
    filteredDevices.value,
    selectedId,
  );
  if (index < 0) return;

  highlightedIndex.value = index;
  await afterScrollerPainted();
  if (!scrollerRef.value) {
    await afterScrollerPainted();
  }
  scrollListToIndex(index);
}

async function scrollListToTop() {
  if (props.loading || !props.active || filteredDevices.value.length === 0) {
    return;
  }
  await afterScrollerPainted();
  if (!scrollerRef.value) {
    await afterScrollerPainted();
  }
  scrollListToIndex(0);
}

async function updateListScrollPosition() {
  const action = resolveTrackerPickerListScrollAction({
    loading: props.loading,
    active: props.active,
    filteredCount: filteredDevices.value.length,
    searchTrim: searchTrim.value,
  });
  if (action === 'top') {
    await scrollListToTop();
    return;
  }
  if (action === 'selected') {
    await scrollToSelectedDevice();
  }
}

// Do not watch modelValue / selection — click select must not move scroll (ENG-2810).
watch(
  () =>
    [
      props.loading,
      props.active,
      props.devices.length,
      searchTrim.value,
      filteredDevices.value.length,
    ] as const,
  async () => {
    await updateListScrollPosition();
  },
  { flush: 'post', immediate: true },
);

function onSearchKeydown(event: KeyboardEvent) {
  if (props.loading || filteredDevices.value.length === 0) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    highlightedIndex.value = nextTrackerPickerHighlightIndex(
      highlightedIndex.value,
      'down',
      filteredDevices.value.length,
    );
    scrollListToIndex(highlightedIndex.value);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    highlightedIndex.value = nextTrackerPickerHighlightIndex(
      highlightedIndex.value,
      'up',
      filteredDevices.value.length,
    );
    scrollListToIndex(highlightedIndex.value);
    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    const device = filteredDevices.value[highlightedIndex.value];
    if (device) {
      selectDevice(device);
    }
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    searchQuery.value = '';
    highlightedIndex.value = -1;
  }
}

function rowClass(deviceId: number, index: number): string {
  if (isSelected(deviceId)) {
    return 'border-gpx_terracotta-100 bg-gpx_terracotta-900 ring-1 ring-inset ring-gpx_terracotta-100';
  }
  if (isKeyboardHighlighted(index)) {
    return 'bg-gray-50 ring-2 ring-inset ring-gray-300';
  }
  return 'bg-white hover:bg-gray-50';
}

function focusSearch(): boolean {
  const root = searchInputRef.value?.$el;
  if (root instanceof HTMLInputElement) {
    root.focus();
    return true;
  }
  if (root instanceof HTMLElement) {
    const input = root.querySelector<HTMLInputElement>(
      'input:not([disabled])',
    );
    if (input) {
      input.focus();
      return true;
    }
  }
  return false;
}

defineExpose({
  focusSearch,
});
</script>

<template>
  <div data-testid="tracker-picker" class="space-y-4">
    <IconField class="w-full">
      <InputIcon class="pi pi-search" />
      <InputText
        ref="searchInputRef"
        v-model="searchQuery"
        class="w-full"
        :placeholder="placeholder"
        fluid
        autocomplete="off"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="!loading && filteredDevices.length > 0"
        @keydown="onSearchKeydown"
      />
    </IconField>

    <div
      v-if="loading"
      class="flex flex-col items-center justify-center py-12"
    >
      <ProgressSpinner
        :style="SPINNER_STYLE_MEDIUM"
        :stroke-width="SPINNER_STROKE_WIDTH"
      />
      <p class="mt-3 text-sm text-gpx_gray-200">{{ loadingLabel }}</p>
    </div>

    <template v-else>
      <div
        v-if="filteredDevices.length === 0"
        class="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 py-12 text-center"
      >
        <p class="text-sm text-gpx_gray-200">
          <template v-if="searchTrim">
            {{ noMatchLabel(searchTrim) }}
          </template>
          <template v-else>{{ emptyLabel }}</template>
        </p>
      </div>
      <VirtualScroller
        v-else
        ref="scrollerRef"
        :items="filteredDevices"
        :item-size="DEVICE_ROW_PX"
        :scroll-height="scrollHeight"
        :tabindex="-1"
        class="overflow-hidden rounded-lg border border-gray-200"
        role="listbox"
      >
        <template #item="{ item: device, options }">
          <button
            type="button"
            tabindex="-1"
            class="select-none flex h-[84px] w-full shrink-0 items-center gap-4 border-b border-gray-100 px-4 py-2 text-left transition-colors last:border-b-0"
            :class="rowClass(device.id, options.index)"
            :aria-pressed="isSelected(device.id)"
            :aria-selected="isKeyboardHighlighted(options.index)"
            @click="toggleSelection(device)"
          >
            <div
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
              :class="
                isSelected(device.id)
                  ? 'bg-gpx_terracotta-100/10'
                  : 'bg-gray-100'
              "
            >
              <i
                class="pi pi-wifi text-base"
                :class="
                  isSelected(device.id)
                    ? 'text-gpx_terracotta-100'
                    : 'text-gray-400'
                "
                aria-hidden="true"
              />
            </div>
            <DeviceOptionContent
              class="min-w-0 flex-1"
              :device="device"
              :search-query="searchTrim"
              :selected="isSelected(device.id)"
            />
            <i
              v-if="isSelected(device.id)"
              class="pi pi-check shrink-0 text-lg text-gpx_terracotta-100"
              aria-hidden="true"
            />
          </button>
        </template>
      </VirtualScroller>
    </template>
  </div>
</template>
